import jwt_decode from "jwt-decode";

import {
  LoginResponseBodyRedirectType,
  LoginResponseBodyType,
  LoginResponseBodyUserType,
} from "@intelligems/ig-types";
import * as Sentry from "@sentry/react";
import {
  QueryObserverOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useQueryParam } from "../../hooks/util";
import { QueryMeta } from "../../types/react-query";
import { JwtToken } from "../../types/user";
import { getAuthToken, Logout } from "../../utils";
import { IgStorage } from "../../utils/igStorage";
import { mpLogin } from "../../utils/mixpanel";
import { api } from "../api";
import { useGetOrg } from "../organizations/get-organization";
import { userKeys } from "../queryKeys";

const login = async (data: { email: string; password: string }) => {
  const resp = await api.post<LoginResponseBodyType>(`/v2/login`, { ...data });
  return resp.data;
};

export const useRefreshShopifyScopes = () => {
  const token = getAuthToken();
  const org = useGetOrg();

  return useMutation<LoginResponseBodyType, Error, any, any>({
    mutationFn: async () => JwtLogin(token!, org.data.org.id, true),
    onSuccess(data) {
      if (data.user["redirect"]) {
        open(data.user["redirect"] as string, "_blank");
      }
    },
  });
};

export const useLogin = () => {
  const queryClient = useQueryClient();
  const widgetAuth = useQueryParam("widgetAuth");

  return useMutation<LoginResponseBodyType, Error, any, any>({
    mutationFn: async (data: { email: string; password: string }) =>
      login(data),
    onSuccess: (data) => {
      data = data as LoginResponseBodyUserType;
      if (widgetAuth) {
        try {
          IgStorage.currentToken = data.token;
        } catch (e) {
          console.warn(e);
        }
      }
      if (data && "redirect" in data) {
        // @ts-ignore
        window.location.href = data.redirect;
      } else if (data && data.user && "redirect" in data.user) {
        data = data as unknown as LoginResponseBodyRedirectType;
        window.location.href = data.user.redirect!;
      } else {
        data = data as LoginResponseBodyUserType;
        IgStorage.authToken = data.token;
        queryClient.setQueryData(userKeys.all, data);

        Sentry.setUser({
          username: data.user.email,
          email: data.user.email,
          name: data.user.lastName
            ? `${data.user.firstName} ${data.user.lastName}`
            : `${data.user.firstName}`,
        });
        mpLogin(data);
      }
    },
  });
};

export const JwtLogin = async (
  token: string,
  orgId?: string,
  reAuth?: boolean
) => {
  const decodedToken = jwt_decode(token) as JwtToken;
  let resp;
  if (!orgId || decodedToken.orgId === orgId) {
    resp = await api.post<LoginResponseBodyUserType>("/v2/login", {
      token,
      reAuth,
    });
  } else {
    resp = await api.post<LoginResponseBodyUserType>("/v2/login", {
      token,
      orgId,
      reAuth,
    });
  }
  return resp.data;
};

export const useLoggedInUser = () => {
  const urlJWT = getAuthToken();
  return useQuery<
    LoginResponseBodyUserType,
    Error,
    LoginResponseBodyUserType,
    any
  >({
    queryKey: userKeys.all,
    queryFn: async () => JwtLogin(urlJWT!),
    enabled: !!urlJWT,
  });
};
export const useUser = (
  options?: QueryObserverOptions<LoginResponseBodyType, Error>
) => {
  const urlParams = new URLSearchParams(window.location.search);
  const widgetAuth = urlParams.get("widgetAuth");
  // org id is out of sync with token, so the other store is requested

  const urlJWT = getAuthToken();
  let orgId: string;
  if (urlJWT) {
    const decoded = jwt_decode(urlJWT!) as JwtToken;
    orgId = decoded.orgId;
  }
  const queryClient = useQueryClient();

  return useQuery<
    LoginResponseBodyUserType,
    Error,
    LoginResponseBodyUserType,
    any
  >({
    queryKey: userKeys.all,
    queryFn: async () => JwtLogin(urlJWT!, widgetAuth || orgId || undefined),
    enabled: urlJWT !== null && urlJWT !== "undefined" && urlJWT !== undefined,
    retry: 3,
    meta: {
      onSuccess: (data: any) => {
        if (data && "redirect" in data) {
          // @ts-ignore
          window.location.href = data.redirect;
        } else if (data && data.user && "redirect" in data.user) {
          // @ts-ignore
          window.location.href = data.user.redirect;
        } else if ("organization" in data && "user" in data) {
          IgStorage.authToken = data.token;
          Sentry.setUser({
            username: data.user.email,
            email: data.user.email,
            name: data.user.lastName
              ? `${data.user.firstName} ${data.user.lastName}`
              : `${data.user.firstName}`,
          });
        }
      },
      onError: {
        any: () => {
          const params = new URLSearchParams(window.location.search);
          if (!params.has("widgetAuth")) {
            queryClient.removeQueries({ queryKey: userKeys.all });
            Logout();
          }
        },
      },
    } as QueryMeta,
  });
};
